/**
 * Which closure calendar a program keeps, from its `programs.type` alone.
 *
 * After-school and enrichment do not share a calendar — see `src/config/blackoutDates.ts`
 * for the three kinds and why the accessors are split. This module is the single place
 * that decides *which* of those questions a given program is asking, so the teacher portal
 * and the parent dashboard cannot answer it differently for the same class.
 *
 * It lives in `utils/` rather than beside `isSessionDate` in `staff-app/` because both an
 * app layer and a shared surface need it, and `utils/` importing from `staff-app/` would
 * invert the layering.
 */

import {
  getBlackoutLabel,
  getEnrichmentClosureLabel,
  isBlackoutDate,
  isEnrichmentClosed,
} from '../config/blackoutDates';
import { DEFAULT_BRAND_KEY, type BrandKey } from '../config/brands';

/**
 * `programs.type` values that are enrichment classes — the hour-long chess, piano, tennis,
 * art, maths and public-speaking blocks that run *inside* the after-school day.
 *
 * An allowlist, not a fallback, and deliberately the opposite choice from
 * `attendanceModeFor`. The full type set is these five plus `after-school`, `camp` and
 * `registration`; an unfamiliar new type therefore falls through to the after-school
 * calendar, which is the safer default — it suppresses sessions on true closures only, and
 * never invents an enrichment cancellation for a program that does not take one.
 */
export const ENRICHMENT_PROGRAM_TYPES: readonly string[] = [
  'sports',
  'artistic',
  'academic',
  'language',
  'logic-strategy',
];

const ENRICHMENT_TYPE_SET: ReadonlySet<string> = new Set(ENRICHMENT_PROGRAM_TYPES);

export function isEnrichmentProgramType(programType: string | null | undefined): boolean {
  return !!programType && ENRICHMENT_TYPE_SET.has(programType.toLowerCase());
}

/**
 * True when the school-wide calendar cancels this program on `ymd`.
 *
 * Camps are exempt entirely: they run on the very days that close after-school, so neither
 * list applies to them. A missing type reads as after-school, which is the conservative
 * answer — it can only ever suppress a day the school is genuinely shut, and never removes
 * a day on which after-school is sold.
 *
 * Per-program vendor days off are *not* here. Those live in `programs.excluded_dates` and
 * are the caller's to apply, because this function only knows about the school.
 */
export function isClosedForProgramType(
  programType: string | null | undefined,
  ymd: string,
  brand: BrandKey = DEFAULT_BRAND_KEY,
): boolean {
  if (programType?.toLowerCase() === 'camp') return false;
  return isEnrichmentProgramType(programType)
    ? isEnrichmentClosed(ymd, brand)
    : isBlackoutDate(ymd, brand);
}

/** Why this program is closed on `ymd`, or undefined when it is not. */
export function closureLabelForProgramType(
  programType: string | null | undefined,
  ymd: string,
  brand: BrandKey = DEFAULT_BRAND_KEY,
): string | undefined {
  if (programType?.toLowerCase() === 'camp') return undefined;
  return isEnrichmentProgramType(programType)
    ? getEnrichmentClosureLabel(ymd, brand)
    : getBlackoutLabel(ymd, brand);
}

/**
 * Billing types whose cron lists the days a family *may* pick, not the days they meet.
 *
 * For these the schedule is a menu and the family's plan is the answer — `per_day` reads
 * `metadata.selected_days`, `flex_monthly` reads that month's `flex_month_plans` row. Every
 * other type answers from its cron and never consults a plan.
 *
 * Shared so the Today card and the week timetable cannot disagree about which enrollments
 * are plan-driven; showing a Flex day the family never bought implies a session they are not
 * paying for.
 */
const DAY_SELECTION_BILLING: ReadonlySet<string> = new Set(['per_day', 'flex_monthly']);

export function isDaySelectionBilling(billingType: string | null | undefined): boolean {
  return DAY_SELECTION_BILLING.has((billingType ?? '').toLowerCase());
}

/**
 * `programs.type` values that carry enrollments and invoices but hold no sessions.
 *
 * A waitlist or a registration fee is a real thing a family bought — it has a row, a price
 * and an invoice — but nobody ever attends it. Its `schedule_cron` is a placeholder (`00 09
 * * * *`, every day) written only to satisfy the column's NOT NULL and its CHECK, and reading
 * that cron as a timetable invents a 9am class seven days a week.
 *
 * The teacher portal has filtered these out of every screen since it was built. This lives
 * here rather than in `staff-app` so the parent surfaces can apply the same rule without
 * `utils/` importing from an app layer.
 */
export const NON_TEACHING_PROGRAM_TYPES: readonly string[] = ['registration'];

const NON_TEACHING_TYPE_SET: ReadonlySet<string> = new Set(NON_TEACHING_PROGRAM_TYPES);

/** PostgREST value list for `.not('type', 'in', …)`, kept beside the array it comes from. */
export const NON_TEACHING_TYPES_FILTER = `(${NON_TEACHING_PROGRAM_TYPES.join(',')})`;

/**
 * True when this program never meets, so no timetable should draw it.
 *
 * A missing type reads as teaching: the failure that matters is a real class vanishing from
 * a parent's week, not a waitlist appearing for one render.
 */
export function isNonTeachingProgramType(programType: string | null | undefined): boolean {
  return !!programType && NON_TEACHING_TYPE_SET.has(programType.toLowerCase());
}
